/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What kind of thing a validation finding says is wrong** (`3i`, `#1412`), declared once, for
 * every validator that reports one.
 *
 * Three readers, and the third is why this is in `libs/dto` rather than the console:
 *
 * 1. **The mapping editors' value pill** shows the kind's short {@link FindingKindDeclaration.label}
 *    in its preview zone — *out of range*, *wrong type* — while the finding's own sentence stays on
 *    the banner and in the severity mark's accessible name (Todd, 2026-09-22).
 * 2. **The level.** A finding names its kind and never its level: the level follows the kind
 *    (`D41`, and Todd, 2026-09-28: *no level for authored fields*). So *how wrong* is decided once
 *    per kind, here, and no validator can disagree with another about the same kind of problem.
 * 3. **Authored validators** (`#1205`) pick a kind from this same list and write their own
 *    message, so system and custom fields report with one vocabulary (Todd, 2026-09-28).
 *
 * **Sized by an audit, not by the concept** (*produce the audit before the declaration*). On
 * 2026-09-28 the editor validators could produce **70 distinct findings**; each was classified by
 * hand, and these sixteen kinds are what they sorted into. Two rulings shaped it (Todd, same day):
 * *Missing* split into {@link FINDING_KINDS.incomplete} (error — the thing cannot work at all) and
 * `missing-value` (warn — a row or field is empty), because the two were already at different levels;
 * and *invalid value* split into four, so the pill says **which** rule the value broke. No kind is at
 * `info`: no validator produced one. And no kind for a *consequence*: a condition with nothing to
 * compare always or never holds, and the pill says **missing**, the problem the author can fix,
 * while the sentence says what it does.
 *
 * **A closed list, because we own it.** *Close over a vocabulary we own.* A new kind is a deliberate
 * addition here, and the `Record` below is what makes every reader handle it.
 */

/** Every kind of finding. */
export const FINDING_KIND_IDS = [
  // ── error: the value or the configuration will not do what was meant ──
  'wrong-type',
  'not-allowed',
  'out-of-range',
  'bad-format',
  'min-over-max',
  'incomplete',
  'ignored',
  'not-available',
  // ── warn: this may not be what was meant ──
  'missing-value',
  'unresolved',
  'conflict',
  'wrong-place',
  'mismatch',
  'at-risk',
  'overridden',
  'unusual-value',
  'metadata-on-wire',
] as const;

export type FindingKind = (typeof FINDING_KIND_IDS)[number];

/**
 * The levels a kind may declare. The console's `MappingIssueLevel` also has `info`, which no kind
 * uses — see the module docblock.
 */
export type FindingKindLevel = 'error' | 'warn';

export interface FindingKindDeclaration {
  /**
   * **How wrong** — `error` is *wrong, and you may proceed*; `warn` is *this may not be what you
   * meant*. Neither blocks anything (`R3a`): blocking is the form validators' short list, not a
   * finding's level.
   */
  readonly level: FindingKindLevel;
  /** The pill's words: two or three, lowercase, naming the problem rather than the rule. */
  readonly label: string;
  /** What a finding of this kind says, for whoever picks one (an authored validator's author). */
  readonly describes: string;
}

export const FINDING_KINDS: Readonly<Record<FindingKind, FindingKindDeclaration>> = {
  'wrong-type': {
    level: 'error',
    label: 'wrong type',
    describes:
      'The value is not the type the destination takes — text where a number goes, a list where one value goes.',
  },
  'not-allowed': {
    level: 'error',
    label: 'not allowed',
    describes: 'The destination accepts a fixed set of values, and this is not one of them.',
  },
  'out-of-range': {
    level: 'error',
    label: 'out of range',
    describes: 'The value is a number outside the range the destination accepts.',
  },
  'bad-format': {
    level: 'error',
    label: 'bad format',
    describes: 'The value does not have the form the destination expects — a pattern, a date, a duration.',
  },
  'min-over-max': {
    level: 'error',
    label: 'min over max',
    describes: 'A minimum is greater than its maximum, so nothing can satisfy both.',
  },
  incomplete: {
    level: 'error',
    label: 'incomplete',
    describes: 'Something the whole thing needs in order to work at all is absent.',
  },
  ignored: {
    level: 'error',
    label: 'ignored',
    describes: 'Nothing reads where this is written, so it has no effect.',
  },
  /*
   * **A refusal, in the product's refusal language** (pattern 42, `#852` slice 4b). Distinct from
   * `ignored`, which is about a place nothing reads: here the place is read, and this value cannot
   * be used there — a round change reading what planning does not hold, or naming a source the round
   * does not call. The row keeps the value; the hover says why and the fix.
   */
  'not-available': {
    level: 'error',
    label: 'not available here',
    describes: 'This place cannot use the value as written; it is kept, and is not applied.',
  },
  'missing-value': {
    level: 'warn',
    label: 'missing',
    describes: 'A row or field has no value.',
  },
  unresolved: {
    level: 'warn',
    label: 'unresolved',
    describes: 'It names something that will resolve to nothing when the request is built.',
  },
  conflict: {
    level: 'warn',
    label: 'conflict',
    describes: 'Two things set here disagree, or cannot both hold.',
  },
  'wrong-place': {
    level: 'warn',
    label: 'wrong place',
    describes: 'The value is set somewhere the receiver does not read it, or belongs in another setting.',
  },
  mismatch: {
    level: 'warn',
    label: 'mismatch',
    describes: 'What is mapped here is a different concept or shape from what the destination declares.',
  },
  'at-risk': {
    level: 'warn',
    label: 'at risk',
    describes: 'Something required may not arrive, because nothing guarantees a value.',
  },
  overridden: {
    level: 'warn',
    label: 'overridden',
    describes: 'Another setting replaces this value before it is sent.',
  },
  'unusual-value': {
    level: 'warn',
    label: 'unusual value',
    describes: 'The value is valid and has a special meaning that is probably not what was meant.',
  },
  'metadata-on-wire': {
    level: 'warn',
    label: 'metadata',
    describes: 'It sends information about our own configuration to the receiver.',
  },
};

/** The level a finding of this kind has — the only way a finding's level is decided. */
export function findingLevel(kind: FindingKind): FindingKindLevel {
  return FINDING_KINDS[kind].level;
}

/** The pill's short label for a finding of this kind. */
export function findingLabel(kind: FindingKind): string {
  return FINDING_KINDS[kind].label;
}
